import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useFormik } from "formik";
import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import {
  APPLICANT_ID_MAX_BYTES,
  APPLICANT_PDF_MAX_BYTES,
  APPLICANT_PHOTO_MAX_BYTES,
  DOCUMENT_TYPES,
  IMAGE_TYPES,
  applicantIdLimit,
  compressApplicantId,
  compressApplicantPhoto,
  formatBytes,
  isImageFile,
} from "utils/uploads";

// TutorRegistration.js and JobPlacement.js were 892 and 821 lines that were
// each one unbroken column of ~20 fields, with the same five-block JSX
// repeated per field and the same upload handlers copied between them. A
// field is a data descriptor here, and the flow is split into steps so an
// applicant on a phone sees a handful of questions at a time.
const FILE_TYPES = { photo: "photo", document: "document" };

const HELPLINE = "09868420099";

// "Please check your connection" is wrong for most of the ways this actually
// fails -- a rules rejection, or the project's file storage being unavailable,
// have nothing to do with the applicant's network, and telling them to retry
// sends them round a loop that cannot succeed. The code Firebase gives back
// distinguishes the cases.
const describeSubmitError = (error) => {
  const code = String(error?.code || "");

  if (code === "storage/unauthorized" || code === "permission-denied") {
    return `Your details were rejected by our server. Please call us on ${HELPLINE} and we will register you directly.`;
  }

  if (
    code === "storage/retry-limit-exceeded" ||
    code === "unavailable" ||
    code === "storage/canceled"
  ) {
    return "The upload timed out. Please check your connection and try again.";
  }

  if (code.startsWith("storage/")) {
    return `We could not upload your documents just now. Please try again shortly, or call us on ${HELPLINE}.`;
  }

  return `We could not submit your registration. Please try again, or call us on ${HELPLINE}.`;
};

const uploadSpec = {
  [FILE_TYPES.photo]: {
    accept: ".png,.jpg,.jpeg,.webp",
    types: IMAGE_TYPES,
    maxBytes: APPLICANT_PHOTO_MAX_BYTES,
    compress: compressApplicantPhoto,
    typeError: "Unsupported file format. Please upload png, jpg, jpeg, or webp.",
    hint: `Photos are reduced to ${formatBytes(APPLICANT_PHOTO_MAX_BYTES)} before they are saved.`,
  },
  [FILE_TYPES.document]: {
    accept: ".png,.jpg,.jpeg,.webp,.pdf",
    types: DOCUMENT_TYPES,
    maxBytes: APPLICANT_ID_MAX_BYTES,
    compress: compressApplicantId,
    typeError: "Unsupported file format. Please upload png, jpg, jpeg, webp, or pdf.",
    hint:
      `ID photos are reduced to ${formatBytes(APPLICANT_ID_MAX_BYTES)}. ` +
      `A PDF must already be under ${formatBytes(APPLICANT_PDF_MAX_BYTES)}.`,
  },
};

function SteppedForm({
  steps,
  initialValues,
  validationSchema,
  onSubmit,
  successTitle,
  successMessage,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [previews, setPreviews] = useState({});
  const previewsRef = useRef(previews);
  previewsRef.current = previews;
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(false);

  const validation = useFormik({
    initialValues,
    validationSchema,
    // Steps are validated by hand on Next, so the whole schema should not run
    // on every keystroke of step one.
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values, helpers) => {
      setSubmitError("");
      try {
        await onSubmit(values);
        helpers.resetForm();
        Object.values(previewsRef.current).forEach((preview) => URL.revokeObjectURL(preview.url));
        setPreviews({});
        setActiveStep(0);
        setDone(true);
      } catch (error) {
        // The old forms announced "Submit Successfully" from a service that
        // swallowed its own errors, so a failed upload looked like a success
        // and the applicant never re-submitted.
        console.error("Could not submit the form:", error);
        setSubmitError(describeSubmitError(error));
      }
    },
  });

  useEffect(
    () => () =>
      Object.values(previewsRef.current).forEach((preview) => URL.revokeObjectURL(preview.url)),
    []
  );

  const stepFieldNames = useMemo(
    () => steps.map((step) => step.fields.map((field) => field.name)),
    [steps]
  );

  const handleFileChange = async (field, file) => {
    if (!file) return;
    const spec = uploadSpec[field.type];

    if (!spec.types.includes(file.type)) {
      validation.setFieldError(field.name, spec.typeError);
      return;
    }

    let processed = file;
    try {
      processed = await spec.compress(file);
    } catch (error) {
      console.error("Error processing file:", error);
      validation.setFieldError(field.name, "Could not process that file. Please try another.");
      return;
    }

    // Compression is best-effort and cannot touch a PDF at all, so an
    // oversized file is refused here rather than failing against the storage
    // rules after the applicant has filled in everything else.
    const limit = file.type === "application/pdf" ? applicantIdLimit(file) : spec.maxBytes;
    if (processed.size > limit) {
      validation.setFieldError(
        field.name,
        `That file is ${formatBytes(processed.size)} after compression. ` +
          `Please upload one under ${formatBytes(limit)}.`
      );
      return;
    }

    validation.setFieldValue(field.name, processed);
    validation.setFieldError(field.name, undefined);

    // An object URL rather than a FileReader data URL: Chrome refuses to
    // render `data:application/pdf` inside an iframe, so a PDF preview built
    // that way is always blank. A blob: URL renders, and works for images too.
    setPreviews((current) => {
      if (current[field.name]) URL.revokeObjectURL(current[field.name].url);
      return {
        ...current,
        [field.name]: {
          url: URL.createObjectURL(processed),
          isImage: isImageFile(processed),
          name: file.name,
          size: processed.size,
        },
      };
    });
  };

  // Nothing above this point stops a field on an earlier step from failing at
  // submit time -- a stale value, or a schema rule that spans fields. Without
  // this, Submit would simply do nothing and the applicant would be stuck on
  // the last step with no visible reason.
  const firstStepWithErrors = (errors) =>
    stepFieldNames.findIndex((names) => names.some((name) => errors[name]));

  const goNext = async () => {
    const errors = await validation.validateForm();
    const names = stepFieldNames[activeStep];
    const failed = names.filter((name) => errors[name]);

    if (failed.length > 0) {
      validation.setTouched({
        ...validation.touched,
        ...Object.fromEntries(names.map((name) => [name, true])),
      });
      return;
    }

    setActiveStep((step) => Math.min(step + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setActiveStep((step) => Math.max(step - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderField = (field) => {
    const { name, label, type, options, required, helper, rows, maxLength, readOnly } = field;
    const error = validation.touched[name] && validation.errors[name];

    const labelNode = (
      <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, mb: 0.75 }}>
        {label}
        {required && <span style={{ color: "#f44335", marginLeft: 3 }}>*</span>}
      </Typography>
    );

    if (type === FILE_TYPES.photo || type === FILE_TYPES.document) {
      const spec = uploadSpec[type];
      const preview = previews[name];

      // A PDF used to collapse to a grey tile reading "PDF selected", so an
      // applicant could not tell whether they had attached the right page --
      // or whether the scan was even legible. Both kinds of file render now.
      const isPdfPreview = Boolean(preview) && !preview.isImage;

      return (
        <FormControl fullWidth>
          {labelNode}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
            <MKBox
              sx={{
                width: 120,
                height: 120,
                flexShrink: 0,
                borderRadius: 2,
                overflow: "hidden",
                display: "grid",
                placeItems: "center",
                bgcolor: ({ palette: { brand } }) => brand.mist,
                border: ({ palette: { brand } }) => `1px solid ${brand.line}`,
              }}
            >
              {preview && preview.isImage && (
                <Box
                  component="img"
                  src={preview.url}
                  alt={label}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
              {isPdfPreview && <PictureAsPdfIcon sx={{ fontSize: 40, color: "#7b809a" }} />}
              {!preview && (
                <Typography
                  sx={{ fontSize: "0.7rem", color: "#7b809a", textAlign: "center", px: 1 }}
                >
                  No file chosen
                </Typography>
              )}
            </MKBox>

            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography sx={{ fontSize: "0.75rem", color: "#7b809a", mb: 0.75 }}>
                {helper || spec.hint}
              </Typography>
              <input
                type="file"
                accept={spec.accept}
                onChange={(event) => handleFileChange(field, event.target.files[0])}
                style={{ fontSize: "0.82rem", maxWidth: "100%" }}
              />

              {preview && (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                  <Typography noWrap sx={{ fontSize: "0.75rem", color: "#7b809a", minWidth: 0 }}>
                    {preview.name} · {formatBytes(preview.size)}
                  </Typography>
                  <MKTypography
                    component="a"
                    href={preview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="button"
                    fontWeight="bold"
                    sx={{
                      flexShrink: 0,
                      color: ({ palette: { brand } }) => brand.navy,
                      textDecoration: "underline",
                    }}
                  >
                    Open full size
                  </MKTypography>
                </Stack>
              )}
            </Box>
          </Stack>

          {isPdfPreview && (
            <MKBox
              component="iframe"
              title={`Preview of ${preview.name}`}
              src={preview.url}
              sx={{
                mt: 2,
                width: "100%",
                height: { xs: 260, sm: 360 },
                borderRadius: 2,
                border: ({ palette: { brand } }) => `1px solid ${brand.line}`,
              }}
            />
          )}

          {error ? <FormHelperText sx={{ color: "#f44335" }}>{error}</FormHelperText> : null}
        </FormControl>
      );
    }

    if (type === "radio") {
      return (
        <FormControl fullWidth>
          {labelNode}
          <RadioGroup
            row
            name={name}
            value={validation.values[name]}
            onChange={validation.handleChange}
          >
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {error ? <FormHelperText sx={{ color: "#f44335" }}>{error}</FormHelperText> : null}
        </FormControl>
      );
    }

    if (type === "checkbox") {
      return (
        <FormControl fullWidth>
          <FormControlLabel
            control={
              <Checkbox
                name={name}
                checked={Boolean(validation.values[name])}
                onChange={validation.handleChange}
              />
            }
            label={<Typography sx={{ fontSize: "0.85rem" }}>{label}</Typography>}
          />
          {error ? <FormHelperText sx={{ color: "#f44335" }}>{error}</FormHelperText> : null}
        </FormControl>
      );
    }

    return (
      <FormControl fullWidth>
        {labelNode}
        <TextField
          fullWidth
          size="small"
          name={name}
          type={type === "textarea" ? "text" : type}
          select={type === "select"}
          multiline={type === "textarea"}
          rows={type === "textarea" ? rows ?? 3 : undefined}
          value={validation.values[name]}
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          error={Boolean(error)}
          inputProps={{ maxLength, readOnly }}
        >
          {type === "select" &&
            options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
        </TextField>
        {helper && !error && <FormHelperText sx={{ color: "#7b809a" }}>{helper}</FormHelperText>}
        {error ? <FormHelperText sx={{ color: "#f44335" }}>{error}</FormHelperText> : null}
      </FormControl>
    );
  };

  if (done) {
    return (
      <Container sx={{ py: { xs: 4, md: 6 } }}>
        <Card
          sx={{
            maxWidth: 560,
            mx: "auto",
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            textAlign: "center",
            boxShadow: "none",
            border: ({ palette: { brand } }) => `1px solid ${brand.line}`,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 56, color: "#4caf50", mb: 2 }} />
          <MKTypography variant="h4" mb={1}>
            {successTitle}
          </MKTypography>
          <MKTypography variant="body2" color="text" mb={3}>
            {successMessage}
          </MKTypography>
          <MKButton variant="gradient" color="primary" onClick={() => setDone(false)}>
            Submit another registration
          </MKButton>
        </Card>
      </Container>
    );
  }

  const isLastStep = activeStep === steps.length - 1;
  const current = steps[activeStep];

  return (
    <Container sx={{ py: { xs: 3, md: 5 } }}>
      <Card
        sx={{
          maxWidth: 900,
          mx: "auto",
          p: { xs: 2.5, md: 4 },
          borderRadius: 3,
          boxShadow: "none",
          border: ({ palette: { brand } }) => `1px solid ${brand.line}`,
        }}
      >
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{ mb: 3, display: { xs: "none", md: "flex" } }}
        >
          {steps.map((step) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <MKBox sx={{ display: { md: "none" }, mb: 3 }}>
          <Typography sx={{ fontSize: "0.75rem", color: "#7b809a", mb: 0.5 }}>
            Step {activeStep + 1} of {steps.length}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={((activeStep + 1) / steps.length) * 100}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </MKBox>

        <MKTypography variant="h5" mb={0.5}>
          {current.label}
        </MKTypography>
        {current.description && (
          <MKTypography variant="button" color="text" display="block" mb={3}>
            {current.description}
          </MKTypography>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}

        <form
          onSubmit={async (event) => {
            event.preventDefault();
            if (!isLastStep) return;

            const errors = await validation.validateForm();
            const failingStep = firstStepWithErrors(errors);

            if (failingStep !== -1) {
              validation.setTouched(
                Object.fromEntries(Object.keys(errors).map((name) => [name, true]))
              );
              setActiveStep(failingStep);
              window.scrollTo({ top: 0, behavior: "smooth" });
              return;
            }

            validation.handleSubmit();
          }}
          noValidate
        >
          {validation.isSubmitting ? (
            <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
              <CircularProgress />
              <MKTypography variant="button" color="text">
                Uploading your details…
              </MKTypography>
            </Stack>
          ) : (
            <>
              <Grid container spacing={2.5}>
                {current.fields.map((field) => (
                  <Grid item xs={12} sm={field.full ? 12 : 6} key={field.name}>
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>

              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                sx={{ mt: 4 }}
              >
                <MKButton
                  variant="outlined"
                  color="primary"
                  onClick={goBack}
                  disabled={activeStep === 0}
                >
                  Back
                </MKButton>

                {isLastStep ? (
                  <MKButton type="submit" variant="gradient" color="primary">
                    Submit registration
                  </MKButton>
                ) : (
                  <MKButton variant="gradient" color="primary" onClick={goNext}>
                    Continue
                  </MKButton>
                )}
              </Stack>
            </>
          )}
        </form>
      </Card>
    </Container>
  );
}

SteppedForm.defaultProps = {
  successTitle: "Registration received",
  successMessage: "Thank you. Our team will be in touch with you shortly.",
};

SteppedForm.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      fields: PropTypes.arrayOf(PropTypes.object).isRequired,
    })
  ).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  initialValues: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  validationSchema: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  successTitle: PropTypes.string,
  successMessage: PropTypes.string,
};

export default SteppedForm;
